import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function StatBox({ amount, label, source }: { amount: string; label: string; source: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 2, textAlign: 'center' }}>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#ff6b6b', lineHeight: 1, mb: 0.5 }}>{amount}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.6)' }}>{source}</Typography>
    </Box>
  );
}

export default function BlogDesPlainePage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Des Plaines Mother's Touch Daycare — When Footage Came Too Late" description="Security footage confirmed the abuse after the fact. An analysis of what real-time behavioral AI detection would have changed in this Illinois case." path="/blog/des-plaines-mothers-touch" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Case Study</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Case Study · 6 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          15 Charges, 7 Boys, 16 Days. What the Cameras at "A Mother's Touch" Already Had.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          A parent reported that someone had sprayed a child in the face with water. That complaint — not any safety system — is what caused the Des Plaines daycare to finally review its own surveillance footage. What they found inside covered 16 days and seven children.
        </Typography>

        {/* YouTube embed */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', mb: 2, boxShadow: '0 0 40px rgba(0,0,0,0.4)' }}>
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/6Z5jQSPGrME"
            title="Des Plaines daycare abuse — FOX 32 Chicago coverage"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.5)', mb: 5, textAlign: 'center' }}>
          FOX 32 Chicago — Bret Buganski reporting on the charges against Jessica Murillo
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        {/* Stats strip */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 6 }}>
          <StatBox amount="15" label="Charges against one worker" source="3 felonies + 12 misdemeanors" />
          <StatBox amount="7" label="Victims — all boys, age 4–5" source="Des Plaines Police" />
          <StatBox amount="16" label="Days the abuse continued" source="November 3–19, 2025" />
        </Stack>

        <Section title="The name on the sign said 'A Mother's Touch'">
          <Para>
            A Mother's Touch Learning Academy. The name is on the sign, on the website, on the intake paperwork that parents signed when they enrolled their 4- and 5-year-old boys. It is the promise the facility made before anyone walked through the door.
          </Para>
          <Para>
            Between November 3 and November 19, 2025, two workers inside that facility — Jessica Murillo, 36, and Izabella Thompson, 23 — were captured on the facility's own surveillance cameras battering seven children across sixteen days. A notebook swung into a child's face. A 4-year-old dragged across the floor by his foot, his head striking the ground. Open-hand strikes to children's faces. Water sprayed directly at children.
          </Para>
          <Para>
            The cameras recorded all of it. For sixteen days.
          </Para>
          <Para>
            No alert fired. No owner was notified. No system was watching. The footage existed and it sat — until a parent called to complain that someone had sprayed water at their child.
          </Para>
        </Section>

        <Section title="How sixteen days of abuse finally came to light">
          <Para>
            On November 20, 2025, a parent contacted the daycare's directors about a specific incident: a worker had sprayed a child in the face with water inside a classroom. That complaint — a single, relatively minor-seeming report — is what prompted the directors to pull up the surveillance system and look.
          </Para>
          <Para>
            What they found covered the previous three weeks. Multiple incidents. Multiple children. Multiple methods. The directors contacted the Illinois Department of Child and Family Services. On November 24, they went to the Des Plaines Police Department with one of the affected parents and filed a formal report.
          </Para>
          <Para>
            By then, Murillo and Thompson had already left for Thanksgiving.
          </Para>

          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderLeft: '3px solid #ff6b6b', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              The trigger was a parent complaint about water-spraying.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              Without that call, no one was reviewing the footage. The surveillance system was running continuously. The evidence was there. The gap was not in the cameras — it was in the absence of any system watching what the cameras recorded.
            </Typography>
          </Box>
        </Section>

        <Section title="What the surveillance footage showed">
          <Para>
            Court documents and police reports describe the following acts captured on camera at A Mother's Touch Learning Academy between November 3–19, 2025:
          </Para>

          <Stack spacing={1.5} sx={{ mb: 3 }}>
            {[
              { act: 'Notebook to the face', detail: 'Murillo struck a child in the face with a notebook. The blow was deliberate, not incidental.' },
              { act: 'Dragged by the foot', detail: 'Murillo dragged a 4-year-old male across the floor by his foot. His head struck the ground during the drag.' },
              { act: 'Open-hand strikes to the face', detail: 'Murillo struck another child in the face multiple times with an open hand.' },
              { act: 'Water sprayed in the face', detail: 'A child was sprayed directly in the face with water inside a classroom — the act that one parent eventually reported.' },
              { act: 'Thompson: additional battery', detail: 'Izabella Thompson, 23, was charged with four counts of misdemeanor battery in connection with separate incidents against the same group of children.' },
            ].map(({ act, detail }) => (
              <Box key={act} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#ff6b6b', mb: 0.5 }}>{act}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>

          <Para>
            Police Chief David Anderson of Des Plaines said: "Our hearts go out to the children and families affected by this troubling case." No children suffered injuries serious enough to require medical attention. That is a statement about how the law categorizes harm — not about what the children experienced.
          </Para>
        </Section>

        <Section title="The charges: 15 counts, three of them felonies">
          <Para>
            Jessica Murillo was charged with 15 counts in total: three felony counts of aggravated battery and twelve misdemeanor battery counts. The felony charges correspond to the three most severe acts captured on camera — the notebook strike, the floor drag, and the repeated open-hand strikes.
          </Para>
          <Para>
            Prosecutors sought to jail Murillo ahead of trial. A judge granted pre-trial release with conditions: no contact with victims or their families, no access to the daycare, and no unsupervised contact with anyone under 18.
          </Para>
          <Para>
            Izabella Thompson, 23, was charged with four misdemeanor battery counts.
          </Para>
          <Para>
            Both workers were terminated. The daycare issued a statement: "The safety and wellbeing of the children in our care is always our top priority. We immediately removed the staff members involved and notified appropriate authorities."
          </Para>
          <Para>
            The statement is true as far as it goes. The directors did act promptly once they reviewed the footage. The question the statement does not answer is why it took a parent's phone call to make anyone look.
          </Para>
        </Section>

        <Section title="What a real-time detection system would have done on November 3rd">
          <Para>
            The first documented incident was November 3. The footage review happened November 20. Between those two dates: seventeen days, seven children, and a series of acts that prosecutors considered serious enough to pursue felony charges.
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                time: 'November 3 — first incident',
                event: 'Motion anomaly detected',
                detail: "An adult's arm making rapid, forceful contact with a small child's face is not a caregiving interaction. SMCLA's behavioral AI identifies the motion signature as an assault event and generates an alert within 60 seconds.",
              },
              {
                time: 'November 3 + 60 seconds',
                event: 'Owner receives clip',
                detail: 'Not hours of footage. A 10-second timestamped clip, the specific room, the specific child. The owner watches it. There is no ambiguity about what they are looking at.',
              },
              {
                time: 'November 3 — same shift',
                event: 'Murillo removed',
                detail: 'Murillo is pulled from the classroom and the facility before the end of the day. Thompson is reviewed. The investigation begins on day one, not day seventeen.',
              },
              {
                time: 'November 4 through 19',
                event: 'Six more children never get hurt',
                detail: 'No dragging. No open-hand strikes. No water sprayed in faces. No parent needs to make a phone call to trigger a footage review that should have happened automatically.',
              },
            ].map(({ time, event, detail }) => (
              <Box key={time} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#ff6b6b', fontWeight: 600 }}>{time}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#f0f0f8', fontWeight: 700 }}>{event}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The pattern across these cases">
          <Para>
            A Mother's Touch in Des Plaines (November 2025). Punkin' Doodles in Lindale, Texas (July–October 2024). The SUNY Cortland Child Care Center (January–April 2026). Three facilities. Three states. Three completely different institutional contexts — a private learning academy, a small-town daycare, a university-operated center.
          </Para>
          <Para>
            The pattern is identical in each one: the surveillance system was running. The footage existed. And discovery depended on something external — a parent complaint, a coworker who happened to review footage, an intern who witnessed something directly.
          </Para>
          <Para>
            In Des Plaines, the gap between the first incident and the footage review was seventeen days. In Lindale, it was three months. In Cortland, it was four months. In none of these cases did an automated system — because none existed — alert anyone in real time.
          </Para>

          <Box sx={{ p: 3, bgcolor: 'rgba(92,225,230,0.04)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: 2, mb: 3, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 16, color: '#5ce1e6', mb: 1 }}>
              The footage is not the problem. The gap between recording and action is.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>
              SMCLA closes that gap. Not by storing more footage, but by watching every frame in real time and acting the moment something is wrong.
            </Typography>
          </Box>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'FOX 32 Chicago — prosecutors release new details in suburban daycare child abuse case', url: 'https://www.fox32chicago.com/news/des-plaines-daycare-employee-charged-battery-granted-pretrial-release' },
              { label: 'Law&Crime — day care workers filmed attacking 7 preschoolers, hitting one in the face with a notebook', url: 'https://lawandcrime.com/crime/day-care-workers-filmed-attacking-7-preschoolers-including-hitting-one-in-the-face-with-a-notebook-police/' },
              { label: 'ABC7 Chicago — Jessica Murillo and Izabella Thompson charged with battering children', url: 'https://abc7chicago.com/post/des-plaines-daycare-arrest-mothers-touch-workers-jessica-murillo-izabella-thompson-charged-battering-children/18645190/' },
              { label: 'NBC Chicago — Des Plaines daycare workers charged with battering kids in care', url: 'https://www.nbcchicago.com/news/local/2-suburban-daycare-workers-face-charges-of-battering-several-children-in-their-care/3900044/' },
              { label: 'Crime Online — 2 Illinois daycare workers charged with battery on 7 children', url: 'https://www.crimeonline.com/2026/02/25/2-illinois-daycare-workers-charged-with-battery-on-7-children/' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#5ce1e6', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Don't wait for a parent to make the call
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            In Des Plaines, a water-spraying complaint was what finally triggered a footage review. SMCLA triggers it automatically — on the first incident, on the first shift, before it becomes sixteen days and seven children.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#4ecfd4' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
